import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, FileText, StickyNote, Lock } from "lucide-react";
import { ChatPanel } from "@/components/ChatPanel";

export default function PastorCoupleDetail() {
  const { coupleId } = useParams();
  const [data, setData] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, [coupleId]);

  const load = () => {
    api
      .get(`/pastor/couples/${coupleId}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Ce couple est introuvable ou vous n'y avez pas accès."));
  };

  const openAnalysis = async (moduleId) => {
    setSelectedModule(moduleId);
    setAnalysis(null);
    setLoadingAnalysis(true);
    try {
      const { data } = await api.get(`/pastor/couples/${coupleId}/analysis/${moduleId}`);
      setAnalysis(data);
    } catch (e) {
      toast.error("Analyse non disponible");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const saveNote = async () => {
    if (!note.trim() || !selectedModule) return;
    setSaving(true);
    try {
      await api.post(`/pastor/couples/${coupleId}/notes`, {
        module_id: selectedModule,
        content: note,
      });
      toast.success("Note privée enregistrée (visible de vous seul).");
      setNote("");
      load();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (!data)
    return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#8A8480]">Chargement…</div>;

  const moduleNotes = (data.notes || []).filter((n) => n.module_id === selectedModule);

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
      <Link
        to="/pastor"
        data-testid="pastor-back-link"
        className="inline-flex items-center gap-2 text-sm text-[#8A8480] hover:text-[#C86B5E] mb-8"
      >
        <ArrowLeft className="w-4 h-4" /> Mes couples
      </Link>

      <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-3">
        {data.couple?.is_solo ? "Suivi · Célibataire (auto-analyse)" : "Suivi pastoral"}
      </p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight mb-2">
        {data.partner_a?.name}
        {!data.couple?.is_solo && data.partner_b && <span className="text-[#8A8480] font-light"> & </span>}
        {!data.couple?.is_solo &&
          (data.partner_b?.name || <span className="text-[#8A8480] italic text-2xl">(en attente du/de la partenaire)</span>)}
      </h1>
      {data.couple?.couple_name && (
        <p className="text-[#5C5754] italic mb-10">{data.couple.couple_name}</p>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Modules list */}
        <div className="lg:col-span-1">
          <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
            Modules
          </p>
          <div className="space-y-2">
            {(data.modules || []).map((m) => (
              <button
                key={m.id}
                data-testid={`pastor-module-${m.id}`}
                onClick={() => m.analysis_ready && openAnalysis(m.id)}
                disabled={!m.analysis_ready}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                  selectedModule === m.id
                    ? "border-[#C86B5E] bg-[#C86B5E]/5"
                    : m.analysis_ready
                      ? "border-[#E6E2DD] bg-white hover:border-[#C86B5E]/40"
                      : "border-[#F3EFEB] bg-[#FAF9F6] opacity-60 cursor-not-allowed"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-heading text-lg leading-tight">{m.title}</span>
                  {m.analysis_ready ? (
                    <CheckCircle2 className="w-4 h-4 text-[#7A8B76]" />
                  ) : (
                    <Clock className="w-4 h-4 text-[#8A8480]" />
                  )}
                </div>
                {m.compatibility_score !== null && m.compatibility_score !== undefined && (
                  <p className="text-xs text-[#7A8B76] mt-1">
                    {m.compatibility_score}% {data.couple?.is_solo ? "de clarté" : "de compatibilité"}
                  </p>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Analysis viewer */}
        <div className="lg:col-span-2">
          {!selectedModule ? (
            <div className="text-center py-20 border border-dashed border-[#E6E2DD] rounded-3xl">
              <FileText className="w-8 h-8 text-[#8A8480] mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-heading text-2xl mb-2">Sélectionnez un module</p>
              <p className="text-[#5C5754]">
                Choisissez un module dont l'analyse est disponible pour la consulter.
              </p>
            </div>
          ) : loadingAnalysis ? (
            <p className="text-[#8A8480]">Chargement…</p>
          ) : analysis ? (
            <div className="space-y-6">
              <div className="bg-white border border-[#E6E2DD] rounded-2xl p-8">
                <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] mb-3">
                  {data.couple?.is_solo ? "Score de clarté" : "Score de compatibilité"}
                </p>
                <p className="font-heading text-5xl text-[#C86B5E]" data-testid="pastor-view-score">
                  {analysis.compatibility_score}%
                </p>
              </div>
              <div className="bg-white border border-[#E6E2DD] rounded-2xl p-8" data-testid="pastor-view-synthesis">
                <h3 className="font-heading text-xl mb-3">Synthèse</h3>
                <p className="text-[#2C2A29] leading-relaxed">{analysis.synthesis}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white border border-[#E6E2DD] rounded-2xl p-6">
                  <h4 className="font-heading text-lg mb-3 text-[#7A8B76]">{data.couple?.is_solo ? "Forces" : "Alignements"}</h4>
                  <ul className="space-y-2 text-sm text-[#5C5754]">
                    {(analysis.alignment_strengths || []).map((s, i) => (
                      <li key={i}>• {s}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white border border-[#E6E2DD] rounded-2xl p-6">
                  <h4 className="font-heading text-lg mb-3 text-[#C86B5E]">{data.couple?.is_solo ? "Points de vigilance" : "À explorer"}</h4>
                  <ul className="space-y-2 text-sm text-[#5C5754]">
                    {(analysis.divergences || []).map((d, i) => (
                      <li key={i}>• {d}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="bg-[#F3EFEB] border border-[#E6DCD3] rounded-2xl p-8">
                <h3 className="font-heading text-xl mb-3">Conseil pastoral & théologique</h3>
                <p className="text-[#2C2A29] leading-relaxed whitespace-pre-wrap font-light">
                  {analysis.biblical_counsel}
                </p>
              </div>

              {/* Notes privées */}
              <div className="bg-white border border-[#E6E2DD] rounded-2xl p-8">
                <div className="flex items-center gap-2 mb-2">
                  <StickyNote className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl">Notes privées</h3>
                  <span className="ml-auto inline-flex items-center gap-1 text-xs text-[#8A8480] bg-[#FAF9F6] border border-[#E6E2DD] rounded-full px-2.5 py-1">
                    <Lock className="w-3 h-3" /> Vous seul
                  </span>
                </div>
                <p className="text-sm text-[#8A8480] mb-4">
                  Ces observations restent confidentielles. Pour échanger avec le couple, utilisez le chat ci-dessous.
                </p>
                {moduleNotes.length > 0 && (
                  <ul className="space-y-3 mb-6">
                    {moduleNotes.map((n) => (
                      <li key={n.id} className="bg-[#FAF9F6] border border-[#E6E2DD] rounded-xl p-4">
                        <p className="text-sm text-[#2C2A29] whitespace-pre-wrap">{n.content}</p>
                        <p className="text-xs text-[#8A8480] mt-2">
                          {new Date(n.created_at).toLocaleString("fr-FR")}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                <textarea
                  data-testid="pastor-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  className="w-full bg-[#FAF9F6] border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3 text-[#2C2A29] resize-none"
                  placeholder="Note privée sur ce module (visible de vous seul)…"
                />
                <button
                  data-testid="pastor-save-note"
                  onClick={saveNote}
                  disabled={!note.trim() || saving}
                  className="mt-3 bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-40 text-white rounded-full px-6 py-2.5 text-sm"
                >
                  {saving ? "…" : "Enregistrer la note privée"}
                </button>
              </div>

              {/* Chat du module */}
              <ChatPanel
                coupleId={coupleId}
                scope={selectedModule}
                scopeLabel={`Chat · ${data.modules.find((m) => m.id === selectedModule)?.title || "Module"}`}
                compact
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Conversation générale */}
      <div className="mt-12">
        <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
          Conversation avec le couple
        </p>
        <ChatPanel coupleId={coupleId} scope="general" scopeLabel="Conversation générale" />
      </div>
    </div>
  );
}
